import React, { useState } from 'react';
import { Form, Input, Button, Card, Alert, message } from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import OtpInput from './merchant/OtpInput';

const LoginForm = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  // Langkah kedua: OTP email. Token baru diterima setelah OTP benar, jadi di sini
  // belum ada apa pun yang disimpan ke localStorage.
  const [otpStep, setOtpStep] = useState(null); // { username, emailMasked, expiresIn }

  // Check if we're in development/localhost mode
  const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
  const showCaptcha = !isDevelopment;

  // Handle CAPTCHA verification
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    setCaptchaVerified(!!token);
  };

  // Handle CAPTCHA expiration
  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
    setCaptchaVerified(false);
  };

  // Simpan sesi admin lalu serahkan ke induk. Dipakai oleh jalur tanpa OTP
  // (saklar darurat) maupun setelah OTP terverifikasi.
  const simpanSesi = (data) => {
    // Bersihkan token mitra hanya bila kita memang di area admin.
    if (window.location.pathname.startsWith('/admin')) {
      localStorage.removeItem('merchant_access_token');
      localStorage.removeItem('merchant_refresh_token');
      localStorage.removeItem('merchant_user');
    }

    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('user', JSON.stringify(data.user));

    message.success('Login successful!');
    if (onLogin) onLogin(data);
  };

  // Dipanggil OtpInput setelah /api/auth/login-verify berhasil.
  const onOtpSuccess = (data) => {
    // Akun marketing punya area sendiri; pemeriksaan yang sama seperti jalur sandi.
    if (data.user?.role === 'marketing') {
      setOtpStep(null);
      setError('Akun marketing tidak dapat masuk ke dashboard admin.');
      message.info('Mengalihkan ke halaman login marketing...');
      setTimeout(() => { window.location.href = '/marketing'; }, 1500);
      return;
    }
    simpanSesi(data);
  };

  const onFinish = async (values) => {
    try {
      // Validate CAPTCHA before proceeding (skip in development)
      if (showCaptcha && (!captchaVerified || !captchaToken)) {
        setError('Please complete the CAPTCHA verification');
        return;
      }

      setLoading(true);
      setError('');

      // Include CAPTCHA token in login request
      const loginData = {
        ...values,
        captchaToken: captchaToken
      };

      const response = await axios.post('/api/auth/login', loginData);

      // Sandi benar tapi belum selesai: tampilkan layar OTP. Tidak ada token
      // yang disimpan pada tahap ini. (Pembersihan token mitra + simpan token
      // admin ditangani simpanSesi setelah OTP terverifikasi.)
      if (response.data.requires_otp) {
        setOtpStep({
          username: response.data.username || values.username,
          emailMasked: response.data.email_masked,
          expiresIn: response.data.otp_expires_in || 300
        });
        setLoading(false);
        if (response.data.otp_sent === false) {
          message.warning('Kode OTP gagal dikirim. Coba tombol kirim ulang.');
        } else {
          message.success('Kode OTP dikirim ke email Anda.');
        }
        return;
      }

      simpanSesi(response.data);

    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Layar OTP: tampil setelah sandi benar, menggantikan formulir sampai selesai.
  if (otpStep) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
        <Card
          style={{
            width: '100%',
            maxWidth: 440,
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            borderRadius: '16px'
          }}
          className="modern-card"
        >
          <OtpInput
            email={otpStep.emailMasked}
            identifier={otpStep.username}
            identifierField="username"
            verifyEndpoint="/api/auth/login-verify"
            resendEndpoint="/api/auth/resend-otp"
            type="login"
            expiresIn={otpStep.expiresIn}
            onSuccess={onOtpSuccess}
            onBack={() => { setOtpStep(null); setError(''); }}
            onCancel={() => { setOtpStep(null); setError(''); }}
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          borderRadius: '16px'
        }}
        className="modern-card"
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)'
          }}>
            <SafetyOutlined style={{ fontSize: '32px', color: '#ffffff' }} />
          </div>
          <h1 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'var(--dark)'
          }}>
            {process.env.REACT_APP_NAME || 'Arendo'}
          </h1>
          <p style={{
            margin: '8px 0 0',
            color: 'var(--gray)',
            fontSize: '14px'
          }}>
            Security Management System
          </p>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Form
          name="login"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            name="username"
            rules={[
              { required: true, message: 'Username is required!' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Username"
              size="large"
              className="modern-form"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Password is required!' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              size="large"
              className="modern-form"
            />
          </Form.Item>

          {showCaptcha && (
            <Form.Item style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEbDZXO4ePnKBB"}
                  onChange={handleCaptchaChange}
                  onExpired={handleCaptchaExpired}
                  theme="light"
                  size="normal"
                />
              </div>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              size="large"
              block
              className="modern-btn"
              disabled={showCaptcha && !captchaVerified}
            >
              Sign In
            </Button>
          </Form.Item>
        </Form>

        <div style={{
          textAlign: 'center',
          marginTop: '24px',
          paddingTop: '24px',
          borderTop: '1px solid var(--gray-light)'
        }}>
          {showCaptcha ? (
            <>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: 'var(--gray)'
              }}>
                🛡️ Verifikasi Keamanan Wajib
              </p>
              <p style={{
                margin: '4px 0 0',
                fontSize: '12px',
                color: 'var(--dark)',
                fontWeight: '600'
              }}>
                Selesaikan CAPTCHA untuk melanjutkan login
              </p>
            </>
          ) : (
            <p style={{
              margin: 0,
              fontSize: '12px',
              color: 'var(--gray)'
            }}>
              🔓 Development Mode - CAPTCHA Disabled
            </p>
          )}
          <p style={{
            margin: '8px 0 0',
            fontSize: '11px',
            color: 'var(--gray)'
          }}>
            Hubungi sistem administrator untuk akses akun
          </p>
        </div>
      </Card>
    </div>
  );
};

export default LoginForm;